
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function PhilosophySection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const philosophies = [
    {
      icon: 'ri-leaf-line',
      title: t('natures_proof'),
      subtitle: t('natures_proof_subtitle'),
      description: t('natures_proof_description')
    },
    {
      icon: 'ri-heart-3-line',
      title: t('empowered_confidence'),
      subtitle: t('empowered_confidence_subtitle'),
      description: t('empowered_confidence_description')
    },
    {
      icon: 'ri-award-line',
      title: t('your_vitality'),
      subtitle: t('your_vitality_subtitle'),
      description: t('your_vitality_description')
    }
  ];

  return (
    <section id="philosophy" ref={sectionRef} className="py-24 lg:py-32 bg-[#F5F1E6]/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 
            className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#2A2A2A] mb-6"
            style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
          >
            {t('philosophy_title')}
          </h2>
          <p 
            className="text-xs sm:text-xs lg:text-base text-[#A6A6A6] max-w-3xl mx-auto"
            style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
          >
            {t('philosophy_subtitle')}
          </p>
        </div>

        {/* Philosophy Cards */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {philosophies.map((item, index) => (
            <div 
              key={index}
              className={`text-center space-y-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${(index + 1) * 200}ms` }}
            >
              {/* Icon */}
              <div className="w-12 h-12 mx-auto bg-white rounded-full flex items-center justify-center shadow-lg">
                <i className={`${item.icon} text-lg text-[#2A2A2A]`}></i>
              </div>

              {/* Title */}
              <div className="space-y-2">
                <h3 
                  className="text-sm sm:text-base font-semibold text-[#2A2A2A]"
                  style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
                >
                  {item.title}
                </h3>
                <p 
                  className="text-xs sm:text-xs lg:text-base text-[#A6A6A6]"
                  style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                >
                  {item.subtitle}
                </p>
              </div>

              {/* Description */}
              <p 
                className="text-xs leading-relaxed"
                style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
              >
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
